/**
 * Фича «Проложить кабель».
 *
 * Кабель начинается кликом по прибору (розетке, выключателю, распредкоробке или
 * светильнику на потолке) — из ниоткуда его не провести. Дальше клики по плану ставят точки трассы по
 * потолку, каждый участок строго горизонтальный или вертикальный: кабель
 * поворачивает только под прямым углом. Клик по другому прибору подключает
 * кабель к нему и записывает его в проект; если прибор не на одной прямой
 * с последней точкой, угол достраивается сам. Esc или правый клик — отменить
 * черновик, ничего не записав. Alt — без привязки к сетке.
 *
 * Точка трассы ставится где угодно, в том числе над мебелью, проёмом, трубой или навесным
 * шкафом: кабель идёт по потолку, а предметы внизу ему не мешают. Такой клик ловим на
 * погружении (`handlePointerDownCapture`) — раньше, чем объект заберёт его себе.
 *
 * Вертикальные спуски от приборов к потолку не чертятся: они следуют из высоты
 * установки прибора и считаются в длине кабеля автоматически.
 *
 * Технику с выводом питания (`powerOutlet` в каталоге: кондиционер) кабель не трогает — концы
 * кабеля только приборы электрики. Клик по ней ставит рядом на той же стене её вывод
 * («Вывод под кондиционер») — или берёт уже стоящий — и кабель идёт от него.
 */
import { useEffect, useRef, useState } from "react";
import type { PointerEvent } from "react";
import type { Pt } from "@/shared/lib/geometry";
import { cableEndPoint, elbowTo, snapOrthogonal } from "@/widgets/plan";
import { cableItemById, getCatalogItem } from "@/shared/catalog";
import { CatalogSectionKind, findFixture, hasCableEnd, itemDimensions } from "@/shared/plan";
import { useFixturesStore } from "@/shared/store/layers";
import { wallLength } from "@/widgets/plan";
import { SelectionKind, Tool, useEditorStore } from "@/shared/store";
import { useWiringStore } from "@/shared/store/layers/wiringStore";
import { useProjectStore } from "@/shared/store";

/** Зазор между техникой и её выводом питания, мм */
const OUTLET_GAP = 100;

/** Технике на стене нужен вывод питания: стена, грань, место техники и какой вывод. */
const powerHost = (fixtureId: string) => {
	const layers = useProjectStore.getState().project.layers;
	const found = findFixture(layers, fixtureId);
	const info = found ? getCatalogItem(found.fixture.ref) : null;
	if (!found || !info?.powerOutlet) return null;
	return { host: found.fixture, info, outlet: info.powerOutlet };
};

/**
 * Вывод питания у техники: уже стоящий рядом на той же грани стены или новый — справа от
 * техники (у конца стены — слева), на её высоте. Новый вывод — свой шаг истории.
 * @returns id вывода; null — это не техника с выводом
 */
const powerOutletFor = (fixtureId: string): string | null => {
	const found = powerHost(fixtureId);
	if (!found) return null;
	const { host, info, outlet } = found;
	const layers = useProjectStore.getState().project.layers;
	const outletRef = { catalog: CatalogSectionKind.Electrical, item: outlet };
	const outletInfo = getCatalogItem(outletRef);
	const wall = layers.walls.walls[host.wallId];
	if (!outletInfo || !wall) return null;
	const hostWidth = itemDimensions(host, info).width;
	const reach = hostWidth / 2 + OUTLET_GAP + outletInfo.width * 2;
	const near = Object.values(layers.electrical.fixtures).find(
		(f) =>
			f.ref.item === outlet &&
			f.wallId === host.wallId &&
			f.side === host.side &&
			Math.abs(f.offset - host.offset) <= reach,
	);
	if (near) return near.id;
	const length = wallLength(layers.walls, wall);
	const gap = hostWidth / 2 + OUTLET_GAP + outletInfo.width / 2;
	const right = host.offset + gap;
	const offset =
		right <= length - outletInfo.width / 2
			? right
			: Math.max(outletInfo.width / 2, host.offset - gap);
	return useProjectStore
		.getState()
		.recordEdit(() =>
			useFixturesStore
				.getState()
				.addFixture(host.wallId, outletRef, offset, host.elevation, host.side),
		);
};

interface Params {
	toWorld: (clientX: number, clientY: number) => Pt;
}

export const useDrawCable = ({ toWorld }: Params) => {
	const tool = useEditorStore((s) => s.tool);
	const gridStep = useEditorStore((s) => s.gridStep);
	const cableMaterial = useEditorStore((s) => s.cableMaterial);
	const cableGroupId = useEditorStore((s) => s.cableGroupId);
	const drawResetToken = useEditorStore((s) => s.drawResetToken);
	const setSelection = useEditorStore((s) => s.setSelection);
	const projectStore = useProjectStore();
	const wiringStore = useWiringStore();

	/** Прибор, от которого ведём; null — кабель не начат */
	const [from, setFrom] = useState<string | null>(null);
	/** Поставленные точки трассы */
	const [points, setPoints] = useState<Pt[]>([]);
	/** Куда ляжет следующая точка под курсором */
	const [preview, setPreview] = useState<Pt | null>(null);
	/** Прибор под курсором — к нему можно подключиться */
	const [hoverFixture, setHoverFixture] = useState<string | null>(null);
	// Дублируем в ref: обработчики читают актуальное значение между рендерами
	const fromRef = useRef<string | null>(null);
	const pointsRef = useRef<Pt[]>([]);

	const active = tool === Tool.Cable;

	const cancel = () => {
		fromRef.current = null;
		pointsRef.current = [];
		setFrom(null);
		setPoints([]);
		setPreview(null);
		setHoverFixture(null);
	};

	// Смена инструмента — черновик забывается
	useEffect(() => {
		cancel();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [drawResetToken, active]);

	/** Трасса черновика: точка начального прибора плюс поставленные точки. */
	const route = (): Pt[] => {
		const start = fromRef.current ? fixturePoint(fromRef.current) : null;
		return start ? [start, ...pointsRef.current] : [];
	};

	/** Точка прибора на плане по актуальному чертежу — на стене или на потолке. */
	const fixturePoint = (id: string): Pt | null => {
		const layers = projectStore.project.layers;
		return cableEndPoint(layers.walls, layers.electrical, id);
	};

	/**
	 * Клик по прибору инструментом кабеля: начать кабель или подключить его.
	 * @returns true, если событие обработано
	 */
	const handleFixturePointerDown = (e: PointerEvent<Element>, fixtureId: string): boolean => {
		if (!active || e.button !== 0) return false;
		e.stopPropagation();
		const layers = projectStore.project.layers;
		// Кликнули по технике (кондиционеру) — кабель идёт от её вывода питания
		const endId = hasCableEnd(layers.electrical, fixtureId) ? fixtureId : powerOutletFor(fixtureId);
		if (!endId) return true;
		return connect(endId);
	};

	/** Начать кабель от прибора или подключить ведомый кабель к нему. */
	const connect = (fixtureId: string): boolean => {
		if (!fromRef.current) {
			fromRef.current = fixtureId;
			setFrom(fixtureId);
			return true;
		}
		if (fromRef.current === fixtureId) return true;

		// Подключаем: достраиваем угол, если прибор не на одной прямой с трассой
		const current = route();
		const target = fixturePoint(fixtureId);
		if (current.length === 0 || !target) return true;
		const last = current[current.length - 1];
		const prev = current.length > 1 ? current[current.length - 2] : null;
		const extra = elbowTo(prev, last, target);
		const startId = fromRef.current;
		// Проложенный кабель — свой шаг истории: Ctrl+Z убирает именно его
		const id = useProjectStore
			.getState()
			.recordEdit(() =>
				wiringStore.addCable(
					startId,
					fixtureId,
					[...pointsRef.current, ...extra],
					cableMaterial ?? undefined,
					cableGroupId ?? undefined,
				),
			);
		cancel();
		if (id) setSelection({ kind: SelectionKind.Cable, id });
		return true;
	};

	/** Клик по плану: поставить точку трассы. @returns true, если событие обработано */
	const handlePointerDown = (e: PointerEvent<SVGSVGElement>): boolean => {
		if (!active || e.button !== 0) return false;
		if (!fromRef.current) return true; // кабель начинается только с прибора
		const current = route();
		if (current.length === 0) return true;
		const last = current[current.length - 1];
		const world = toWorld(e.clientX, e.clientY);
		const next = e.altKey ? world : snapOrthogonal(last, world, gridStep);
		if (Math.abs(next.x - last.x) < 1 && Math.abs(next.y - last.y) < 1) return true;
		pointsRef.current = [...pointsRef.current, next];
		setPoints(pointsRef.current);
		return true;
	};

	/** Прибор под курсором, к которому можно подключить кабель; null — такого нет. */
	const endUnder = (target: EventTarget): string | null => {
		const id =
			(target as Element).closest?.('[data-role="fixture"][data-id]')?.getAttribute("data-id") ??
			null;
		if (!id || id === fromRef.current) return null;
		return hasCableEnd(projectStore.project.layers.electrical, id) || powerHost(id) ? id : null;
	};

	/**
	 * Клик на погружении, пока кабель ведётся: всё, что не прибор для подключения, — точка
	 * трассы, даже если под курсором диван или шкаф. @returns true, если событие обработано
	 */
	const handlePointerDownCapture = (e: PointerEvent<SVGSVGElement>): boolean => {
		if (!active || !fromRef.current || e.button !== 0 || e.shiftKey) return false;
		// Второй палец — щипок, его разбирает холст
		if (e.pointerType === "touch" && !e.isPrimary) return false;
		if (endUnder(e.target)) return false;
		e.stopPropagation();
		return handlePointerDown(e);
	};

	const handlePointerMove = (e: PointerEvent<SVGSVGElement>) => {
		if (!active || !fromRef.current) return;
		const over = endUnder(e.target);
		setHoverFixture(over);

		const current = route();
		if (current.length === 0) return;
		const last = current[current.length - 1];
		if (over) {
			// Над прибором показываем, что кабель подключится к нему
			setPreview(fixturePoint(over));
			return;
		}
		const world = toWorld(e.clientX, e.clientY);
		setPreview(e.altKey ? world : snapOrthogonal(last, world, gridStep));
	};

	const draftRoute = route();
	const hint = !active
		? null
		: !from
			? "Кликните по розетке, выключателю, светильнику, распредкоробке, щитку или кондиционеру — оттуда пойдёт кабель"
			: hoverFixture
				? "Клик — подключить кабель к этому прибору"
				: "Клик по плану — точка трассы (только под прямым углом), клик по прибору — подключить. Esc — отменить";

	return {
		active,
		from,
		points,
		/** Трасса черновика от прибора через точки; пустая, если кабель не начат */
		route: draftRoute,
		preview,
		color: (cableMaterial && cableItemById(cableMaterial)?.color) || "#64748b",
		canFinish: hoverFixture !== null,
		hint,
		cancel,
		handleFixturePointerDown,
		handlePointerDown,
		handlePointerDownCapture,
		handlePointerMove,
	};
};
